
import { Vue, Component, Prop } from 'vue-property-decorator';
import { mapState } from 'vuex';
import axios from 'axios';
import { Currencies } from '@/Models/Localization';
import TrackingScripts from './TrackingScripts.vue';
import { SubscriptionState, Plan, Subscription } from '@/classes/stripe';
import AppConfig from '@/AppConfig';
import Icon from '@/components/Icons/Icon.vue';
import EmptyState from '@/components/EmptyState.vue';
import moment from 'moment';
import store from '@/store';

@Component({
  components: {
    TrackingScripts,
    Icon,
    EmptyState,
  },
  computed: {
    ...mapState<SubscriptionState>('subscription', {
      subscriptionPlan: (state: SubscriptionState) => state.selectedPlan,
      activeSubscription: (state: SubscriptionState) => state.activeSubscription,
    }),
  },
})
export default class PaymentComplete extends Vue {
  @Prop({ default: 'new' })
  type!: string;

  // state from vuex
  subscriptionPlan!: Plan;
  activeSubscription!: Subscription;
  team = store.state.auth.team;

  // what type of subscription (downgrade or upgrade)
  subscriptionType = '';

  // selected subscription plan
  newPlan: Plan = new Plan();

  revenueInEUR: number = 0;

  apiBaseUrl: string = AppConfig.getAPIBaseUrl() || '';

  // Setup the components needed data, like customer data.
  async created(): Promise<void> {
    // Without a plan a checkout is not possible so we redirect the customer to the selection page
    if (!this.subscriptionPlan.id) {
      await this.$router.push({ name: 'checkout_plan_selection' });
      return;
    }

    // Load plan from vuex
    Object.assign(this.newPlan, this.subscriptionPlan);

    this.subscriptionType = this.$router.currentRoute.params.type;
  }

  async mounted(): Promise<void> {
    await this.convertToEUR(
      this.$store.state.subscription.selectedPlan.price.price,
      this.$store.state.auth.team.currency
    );
    this.pushDataLayerToGtm();

    // Clear selectedPlan after checkout is completely done
    await this.$store.dispatch('subscription/resetSelectedPlan');
  }

  /**
   * Converts the given price (with currency) to EUR
   * @param price
   * @param currency
   */
  async convertToEUR(price: number, currency: string): Promise<void> {
    if (currency !== 'eur') {
      await axios
        .post(this.apiBaseUrl.concat('/currency/convert/'), {
          value: price,
          currency: currency,
          returnCurrency: Currencies.EUR.toUpperCase(),
        })
        .then((resp: any) => {
          if (resp.data) {
            this.revenueInEUR = resp.data.value;
          }
        })
        .catch((error) => console.error('Failed converting to EUR with the following error: ' + error));
    } else {
      this.revenueInEUR = price;
    }
  }

  activeSubscriptionEndDate(): string {
    return moment(this.activeSubscription.ends_at).format('MMMM D, YYYY');
  }

  pushDataLayerToGtm(): void {
    //@ts-ignore
    window.dataLayer = window.dataLayer || [];
    //@ts-ignore
    window.dataLayer.push({
      event: 'checkout_complete',
      user: [
        {
          id: this.$store.state.auth.user?.id,
          email: this.$store.state.auth.user?.email,
          is_team_lead: this.$store.state.auth.user?.is_team_lead,
          created_at: this.$store.state.auth.user?.created_at?.date,
          updated_at: this.$store.state.auth.user?.updated_at?.date,
          first_name: this.$store.state.auth.user?.first_name,
          last_name: this.$store.state.auth.user?.last_name,
          region: this.$store.state.auth.user?.team?.state,
          street: `${this.$store.state.auth.user?.team?.line1} ${this.$store.state.auth.user?.team?.line2}`,
          postcode: this.$store.state.auth.user?.team?.postal_code,
          city: this.$store.state.auth.user?.team?.city,
          country: this.$store.state.auth.user?.team?.country,
        },
      ],
      account: [
        {
          id: this.$store.state.account.account?.id,
          trial: this.$store.state.account.account?.trial,
          trial_date: this.$store.state.account.account?.trial_date,
          type: this.$store.state.account.account?.type,
          payment_open: this.$store.state.account.account?.payment_open,
          payment_overdue: this.$store.state.account.account?.payment_overdue,
          status: this.$store.state.account.account?.status,
          payment_creditcard_expired: this.$store.state.account.account?.payment_creditcard_expired,
          trial_left: this.$store.state.account.account?.trial_left,
          qrcodes_current: this.$store.state.account.account?.qrcodes?.current,
          qrcodes_allowed: this.$store.state.account.account?.qrcodes?.allowed,
          industry_id: this.$store.state.account.account?.industry_id,
          material_id: this.$store.state.account.account?.material_id,
        },
      ],
      statistics: [
        {
          active: this.$store.state.account.account?.statistics?.active,
          paused: this.$store.state.account.account?.statistics?.paused,
          all: this.$store.state.account.account?.statistics?.all,
          expired: this.$store.state.account.account?.statistics?.expired,
          scans: this.$store.state.account.account?.statistics?.scans,
          designs: this.$store.state.account.account?.statistics?.designs,
        },
      ],
      active_subscription: [
        {
          id: this.$store.state.subscription.activeSubscription?.id,
          created_at: this.$store.state.subscription.activeSubscription?.created_at?.date,
          updated_at: this.$store.state.subscription.activeSubscription?.updated_at?.date,
          plan_id: this.$store.state.subscription.activeSubscription?.plan_id,
          new_plan_id: this.$store.state.subscription.activeSubscription?.new_plan_id,
          is_on_invoice: this.$store.state.subscription.activeSubscription?.is_on_invoice,
          is_cancelled: this.$store.state.subscription.activeSubscription?.is_cancelled,
          is_active: this.$store.state.subscription.activeSubscription?.is_active,
          ends_at: this.$store.state.subscription.activeSubscription?.ends_at,
        },
      ],
      selected_plan: [
        {
          oldPlanId: this.$store.state.subscription.selectedPlan?.oldPlanId,
          id: this.$store.state.subscription.selectedPlan?.id,
          title: this.$store.state.subscription.selectedPlan?.title,
          subtitle: this.$store.state.subscription.selectedPlan?.subtitle,
          code_limit: this.$store.state.subscription.selectedPlan?.codeLimit,
          scan_limit: this.$store.state.subscription.selectedPlan?.scanLimit,
          bulk_limit: this.$store.state.subscription.selectedPlan?.bulkLimit,
          api_limit: this.$store.state.subscription.selectedPlan?.apiLimit,
          user_limit: this.$store.state.subscription.selectedPlan?.userLimit,
          premium_support: this.$store.state.subscription.selectedPlan?.hasPremiumSupport,
          price: this.$store.state.subscription.selectedPlan?.price?.price,
          price_currency: this.$store.state.subscription.selectedPlan?.price?.currency,
          price_locale: this.$store.state.subscription.selectedPlan?.price?.locale,
          price_symbol: this.$store.state.subscription.selectedPlan?.price?.symbol,
          price_converted_eur: this.revenueInEUR,
        },
      ],
    });
  }
}
